import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";
import { Box, Button, Typography, Chip, Paper, Stack } from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  alpha,
} from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import CategoryIcon from "@mui/icons-material/Category";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2563EB", light: "#DBEAFE", dark: "#1D4ED8" },
    background: { default: "#F0F2F8", paper: "#FFFFFF" },
    text: { primary: "#0F1117", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: {
    fontFamily: "'Sora',sans-serif",
    h2: { fontFamily: "'DM Serif Display',serif", fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

const fadeUp = keyframes`from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}`;
const spin = keyframes`to{transform:rotate(360deg)}`;
const skeletonPulse = keyframes`0%,100%{opacity:1}50%{opacity:.4}`;

const PageWrapper = styled(Box)(() => ({
  minHeight: "100vh",
  background: "linear-gradient(145deg,#EEF2FF 0%,#F0F2F8 40%,#E8EDF8 100%)",
  padding: "28px 20px 60px",
  fontFamily: "'Sora',sans-serif",
  "&::before": {
    content: '""',
    position: "fixed",
    top: -120,
    right: -120,
    width: 480,
    height: 480,
    borderRadius: "50%",
    background:
      "radial-gradient(circle,rgba(37,99,235,.06) 0%,transparent 70%)",
    pointerEvents: "none",
  },
}));

const ContentBox = styled(Box)({ maxWidth: 760, margin: "0 auto" });

const SectionCard = styled(Paper, { shouldForwardProp: (p) => p !== "delay" })(
  ({ theme, delay }) => ({
    borderRadius: 16,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    boxShadow: "0 1px 3px rgba(0,0,0,.05),0 8px 24px rgba(0,0,0,.04)",
    padding: "24px 28px",
    marginBottom: 20,
    animation: `${fadeUp} .5s ease both`,
    animationDelay: `${delay ?? 0}ms`,
    transition: "box-shadow .2s ease",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(37,99,235,.06),0 12px 32px rgba(0,0,0,.06)",
    },
  })
);

const CardLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: ".9px",
  color: theme.palette.text.disabled,
  marginBottom: 18,
  display: "flex",
  alignItems: "center",
  gap: 8,
  "&::before": {
    content: '""',
    display: "inline-block",
    width: 3,
    height: 14,
    background: theme.palette.primary.main,
    borderRadius: 2,
  },
}));

const BackBtn = styled(Button)(({ theme }) => ({
  borderRadius: 40,
  padding: "7px 18px 7px 12px",
  fontSize: 13,
  fontWeight: 600,
  textTransform: "none",
  color: theme.palette.text.secondary,
  border: `1px solid ${alpha("#000", 0.12)}`,
  background: "#fff",
  transition: "all .2s ease",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    transform: "translateX(-2px)",
  },
}));

const PrimaryBtn = styled(Button)(() => ({
  background: "linear-gradient(135deg,#2563EB 0%,#1E40AF 100%)",
  color: "#fff",
  borderRadius: 10,
  padding: "11px 28px",
  fontSize: 14,
  fontWeight: 700,
  textTransform: "none",
  fontFamily: "'Sora',sans-serif",
  boxShadow: "0 4px 14px rgba(37,99,235,.28)",
  transition: "all .2s ease",
  "&:hover": {
    background: "linear-gradient(135deg,#1D4ED8 0%,#1E3A8A 100%)",
    boxShadow: "0 6px 20px rgba(37,99,235,.38)",
    transform: "translateY(-1px)",
  },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": {
    background: "#E5E7EB",
    color: "#9CA3AF",
    boxShadow: "none",
  },
}));

const StyledTextArea = styled("textarea")(() => ({
  width: "100%",
  fontFamily: "'Sora',sans-serif",
  fontSize: 14,
  color: "#0F1117",
  background: alpha("#F0F2F8", 0.8),
  border: `1px solid ${alpha("#000", 0.1)}`,
  borderRadius: 10,
  padding: "12px 14px",
  outline: "none",
  boxSizing: "border-box",
  resize: "vertical",
  lineHeight: 1.65,
  transition: "all .2s ease",
  "&:focus": {
    borderColor: "#2563EB",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(37,99,235,.08)",
  },
  "&::placeholder": { color: "#9CA3AF" },
}));

const StyledSelect = styled("select")(() => ({
  width: "100%",
  fontFamily: "'Sora',sans-serif",
  fontSize: 14,
  color: "#0F1117",
  background: alpha("#F0F2F8", 0.8),
  border: `1px solid ${alpha("#000", 0.1)}`,
  borderRadius: 10,
  padding: "11px 14px 11px 36px",
  outline: "none",
  boxSizing: "border-box",
  appearance: "none",
  cursor: "pointer",
  transition: "all .2s ease",
  "&:focus": {
    borderColor: "#2563EB",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(37,99,235,.08)",
  },
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: ".6px",
  textTransform: "uppercase",
  color: "#9CA3AF",
  marginBottom: 6,
  fontFamily: "'Sora',sans-serif",
}));

const Spinner = styled(Box)(() => ({
  width: 16,
  height: 16,
  border: "2px solid rgba(255,255,255,.3)",
  borderTop: "2px solid #fff",
  borderRadius: "50%",
  animation: `${spin} .7s linear infinite`,
  display: "inline-block",
  flexShrink: 0,
}));

const Sb = ({ w = "100%", h = 14, r = 6 }) => (
  <Box
    sx={{
      width: w,
      height: h,
      borderRadius: r,
      background: alpha("#000", 0.07),
      animation: `${skeletonPulse} 1.5s ease-in-out infinite`,
    }}
  />
);

const AdminNewsEditPage = () => {
  const { newsId } = useParams();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [image, setImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchNewsDetails = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/new/${newsId}/`);
      setTitle(res.data.title);
      setDescription(res.data.description);
      setCategory(res.data.category);
      setImage(res.data.news_img);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching news:", err);
      setLoading(false);
    }
  }, [newsId]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/newscategory/`);
      setCategories(res.data);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  useEffect(() => {
    fetchNewsDetails();
    fetchCategories();
  }, [fetchNewsDetails]);

  const updateNews = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/new/${newsId}/`, {
        title,
        description,
        category,
      });
      navigate(-1);
    } catch (err) {
      console.error("Error updating news:", err);
      setSaving(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&family=DM+Serif+Display&display=swap"
        rel="stylesheet"
      />
      <PageWrapper>
        <ContentBox>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 4,
              animation: `${fadeUp} .4s ease both`,
              flexWrap: "wrap",
            }}
          >
            <BackBtn
              startIcon={<ArrowBackIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => navigate(-1)}
            >
              Back
            </BackBtn>
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: 22, sm: 27 },
                  color: "text.primary",
                  lineHeight: 1.1,
                }}
              >
                Edit Article
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "text.disabled",
                  mt: 0.3,
                  fontFamily: "'Sora',sans-serif",
                }}
              >
                Article ID:{" "}
                <strong style={{ color: "#4B5563" }}>{newsId}</strong>
              </Typography>
            </Box>
            <Chip
              label="Editing"
              size="small"
              sx={{
                ml: "auto",
                fontFamily: "'Sora',sans-serif",
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: ".5px",
                textTransform: "uppercase",
                background: alpha("#C4922A", 0.12),
                color: "#C4922A",
                border: `1px solid ${alpha("#C4922A", 0.25)}`,
              }}
            />
          </Box>

          <Box component="form" onSubmit={updateNews}>
            {/* Fields Card */}
            <SectionCard delay={60}>
              <CardLabel>Article Details</CardLabel>

              {loading ? (
                <Stack spacing={2.5}>
                  <Box>
                    <Sb w="20%" h={11} />
                    <Box sx={{ mt: 0.8 }}>
                      <Sb w="100%" h={80} r={10} />
                    </Box>
                  </Box>
                  <Box>
                    <Sb w="20%" h={11} />
                    <Box sx={{ mt: 0.8 }}>
                      <Sb w="100%" h={120} r={10} />
                    </Box>
                  </Box>
                  <Box>
                    <Sb w="15%" h={11} />
                    <Box sx={{ mt: 0.8 }}>
                      <Sb w="50%" h={44} r={10} />
                    </Box>
                  </Box>
                </Stack>
              ) : (
                <>
                  <Box sx={{ mb: 2.5 }}>
                    <FieldLabel>Title *</FieldLabel>
                    <StyledTextArea
                      rows={2}
                      placeholder="Enter news title…"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </Box>
                  <Box sx={{ mb: 2.5 }}>
                    <FieldLabel>Description *</FieldLabel>
                    <StyledTextArea
                      rows={5}
                      placeholder="Enter description…"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </Box>
                  <Box sx={{ mb: 0, maxWidth: 300 }}>
                    <FieldLabel>Category *</FieldLabel>
                    <Box sx={{ position: "relative" }}>
                      <CategoryIcon
                        sx={{
                          position: "absolute",
                          left: 10,
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: 15,
                          color: "text.disabled",
                          pointerEvents: "none",
                        }}
                      />
                      <StyledSelect
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                      >
                        <option value="" disabled>
                          Select a category
                        </option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                      </StyledSelect>
                      <ArrowForwardIosIcon
                        sx={{
                          position: "absolute",
                          right: 10,
                          top: "50%",
                          transform: "translateY(-50%) rotate(90deg)",
                          fontSize: 11,
                          color: "text.disabled",
                          pointerEvents: "none",
                        }}
                      />
                    </Box>
                  </Box>
                </>
              )}
            </SectionCard>

            {/* Current Image Card */}
            {!loading && image && (
              <SectionCard delay={110}>
                <CardLabel>Current Cover Image</CardLabel>
                <Box
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    border: `1px solid ${alpha("#000", 0.07)}`,
                    maxHeight: 260,
                  }}
                >
                  <Box
                    component="img"
                    src={image}
                    alt="News preview"
                    sx={{
                      width: "100%",
                      maxHeight: 260,
                      objectFit: "cover",
                      display: "block",
                    }}
                    onError={(e) => (e.target.style.display = "none")}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "text.disabled",
                    fontFamily: "'Sora',sans-serif",
                    mt: 1,
                  }}
                >
                  Image editing is not supported here. The current image will be
                  kept.
                </Typography>
              </SectionCard>
            )}

            {/* Actions */}
            {!loading && (
              <Box
                sx={{
                  animation: `${fadeUp} .5s ease both`,
                  animationDelay: "160ms",
                }}
              >
                <Stack direction="row" spacing={1.5}>
                  <PrimaryBtn
                    type="submit"
                    startIcon={
                      saving ? (
                        <Spinner />
                      ) : (
                        <CheckIcon sx={{ fontSize: "16px !important" }} />
                      )
                    }
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Update Article"}
                  </PrimaryBtn>
                  <Button
                    onClick={() => navigate(-1)}
                    sx={{
                      borderRadius: 2.5,
                      fontSize: 14,
                      fontWeight: 600,
                      textTransform: "none",
                      border: `1px solid ${alpha("#000", 0.12)}`,
                      color: "text.secondary",
                      px: 2.5,
                      fontFamily: "'Sora',sans-serif",
                      "&:hover": { background: alpha("#000", 0.04) },
                    }}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Box>
            )}
          </Box>
        </ContentBox>
      </PageWrapper>
    </ThemeProvider>
  );
};

export default AdminNewsEditPage;
